import { IUser } from "@home-health-notify/domain";
import { Link, Typography } from "@mui/material";
import { AuthContext } from "@home-health-notify/auth";
import { DomainContext, useTranslation } from "hhn-commons";
import { useContext } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router";
import { DocumentMediaViewerProvider } from "../context/DocumentMediaViewerProvider";
import { HomePageWrapper } from "../home/HomePage";
import Loader from "../loader/Loader";
import { ROUTES } from "../routes";
import { loadOrganizationId } from "../utils/organizationIdStorage";
import { clearPendingRoute } from "../utils/pendingRouteStorage";
import { OrganizationList } from "./OrganizationList";
import { NoOrganizationContent } from "./OrganizationList.style";

function Organizations() {
  const navigate = useNavigate();
  const { domain } = useContext(DomainContext);
  const user: IUser | null = domain.user;
  const orgUsers = user?.getOrganizations() || [];
  // Federated: clears Cognito's SSO cookie too, so the next sign-in
  // re-prompts instead of silently restoring this account.
  const { signOutFederated: logoutUser } = useContext(AuthContext);
  const isOrganizationListEmpty = orgUsers && orgUsers.length === 0;

  // Show loader if domain is UnknownAuthenticationDomain
  const isUnknownDomain = domain.domainType === "unknown";
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "organization" });
  const storedOrgId = loadOrganizationId();
  // Auto-navigate if: stored org ID exists OR user has only one org
  // Otherwise show org list so user can choose
  const singleOrgId =
    orgUsers?.length === 1
      ? orgUsers[0]?.getOrganization()?.organizationId
      : undefined;
  const knownOrgId = storedOrgId || singleOrgId;

  if (isUnknownDomain) return <Loader />;

  const onLogout = async () => {
    await logoutUser();
    clearPendingRoute();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <DocumentMediaViewerProvider>
      <>
        {!user && <Loader />}
        {isOrganizationListEmpty && (
          <NoOrganizationContent>
            <Typography>{tNs("noOrgAssigned")}</Typography>
            <Typography>
              {tNs("contactTo")}
              <Link
                href="mailto:support@homehealthnotify.com"
                target="_top"
                rel="noopener noreferrer"
              >
                {t("supportEmailId")}
              </Link>
            </Typography>
          </NoOrganizationContent>
        )}
      </>
      <Routes>
        <Route
          index
          element={
            knownOrgId ? (
              <Navigate
                to={`${ROUTES.ORGANIZATIONS}/${knownOrgId}/${ROUTES.CHATS}`}
                replace
              />
            ) : (
              <OrganizationList orgUsers={orgUsers} onLogout={onLogout} />
            )
          }
        />
        <Route
          path=":orgId/*"
          element={<HomePageWrapper onLogout={onLogout} />}
        ></Route>
      </Routes>
    </DocumentMediaViewerProvider>
  );
}

export default Organizations;
